import React, { createContext, useContext, useEffect, useState } from "react";
// import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/router";
import { blobToBase64, getTemplate, uploadPhoto } from "@/api/http";
import { useStorage } from "@/utils/storage";
import { STEPS, HANDOFF_METHODS, PRODUCT_PATHS } from "@/api/constants";
import { Document } from "./document";

interface Template {
    success: boolean;
    templateType: string;
    provider: string;
    redirectUrl: string | null;
    alreadyVerified: boolean;
    isIdEnabled: boolean;
    nextStep: STEPS;
}

export interface StoreState {
    leadId: string;
    barcode: string /* base64-encoded barcode data */;
    blobs: Blob[] /* Maintain a list of image Blobs */;
    nextStep: STEPS;
    clientToken: string;
    tokenCreated: number /* time in ms since epoch (midnight at the beginning of January 1, 1970, UTC) */;
    templateType: string;
    error: string;
    loading: boolean;
    hydrated: boolean;
    transactionCreated: boolean;
    hasCreditConsent: boolean;
    handoffMethod: HANDOFF_METHODS;
    template: Template;
}

interface IUploadImagesResponse {
    success?: boolean;
    issues?: string[];
    issueDetails?: Record<string, unknown>;
    nextStep?: string;
    error?: any;
}

const initialContext = {
    state: {
        leadId: "",
        barcode: "",
        blobs: [],
        nextStep: STEPS.UPLOAD_DOCUMENT_FRONT,
        clientToken: "",
        tokenCreated: 0,
        templateType: "",
        error: "",
        loading: false,
        hydrated: false,
        transactionCreated: false,
        hasCreditConsent: false,
        handoffMethod: HANDOFF_METHODS.PHONE,
        template: {} as any,
    } as StoreState,
    actions: {
        setBarcode: (data: string) => console.log(data),
        setBlobs: (data: Blob[]) => console.log(data),
        setTransactionCreated: (flag: boolean) => console.log(flag),
        setHasCreditConsent: (flag: boolean) => console.log(flag),
        setNextStep: (step: string) => console.log(step),
        setClientToken: (v: string) => console.log(v),
        setTokenCreated: (v: number) => console.log(v),
        setTemplateType: (v: string) => console.log(v),
        setHandoffMethod: (method: HANDOFF_METHODS) => console.log(method),

        uploadPhoto: () =>
            new Promise<IUploadImagesResponse>((resolve) => resolve({})),
        initiateTransaction: () => {},
        reset: () => {},
        loadTemplate: () => ({}) as Template,
    },
};

const StoreContext = createContext(initialContext);

export function StoreProvider(props: any) {
    if (!props) {
        throw new Error("Global store props undefined!");
    }
    const router = useRouter();

    let leadId = "";
    if (!props.leadId && !props.url) {
        leadId = router.asPath.split("/").filter(Boolean)[1] ?? "";
    } else if (!props.leadId && props.url) {
        leadId = props.url.split("/")[2];
    } else {
        leadId = props.leadId;
    }

    const [barcode, setBarcode] = useStorage<string>("barcode", "");
    const [blobs, setBlobs] = useStorage<Blob[]>("blobsData", []);
    const [transactionCreated, setTransactionCreated] = useStorage<boolean>(
        "transactionCreated",
        false,
    );
    const [hasCreditConsent, setHasCreditConsent] = useStorage<boolean>(
        "hasCreditConsent",
        false,
    );
    const [nextStep, _setNextStep] = useStorage<STEPS>(
        "nextStep",
        STEPS.UPLOAD_DOCUMENT_FRONT,
    );
    const [clientToken, setClientToken] = useStorage<string>("clientToken", "");
    const [tokenCreated, setTokenCreated] = useStorage<number>(
        "tokenCreated",
        0,
    );
    const [templateType, setTemplateType] = useStorage<string>(
        "templateType",
        "",
    );
    const [handoffMethod, setHandoffMethod] = useState<HANDOFF_METHODS>(
        HANDOFF_METHODS.PHONE,
    );
    const [error, setError] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [hydrated, setHydrated] = useState<boolean>(false);

    const [template, setTemplate] = useState<any>(null);

    const setNextStep = (step: STEPS) => {
        _setNextStep(step || STEPS.UPLOAD_DOCUMENT_FRONT);
    };

    const loadTemplate = async () => {
        const isAvaId =
            router.pathname.split("/").filter(Boolean)[0] ===
            PRODUCT_PATHS.AVA_ID;

        if (!isAvaId) return;

        if (template) {
            return template;
        }

        const t = await getTemplate(leadId);
        setTemplate(t);
        return t;
    };

    useEffect(() => {
        loadTemplate();
    }, [leadId]);

    const reset = () => {
        setBarcode("");
        setBlobs([]);
        setTransactionCreated(false);
        setHasCreditConsent(false);
        setNextStep(STEPS.UPLOAD_DOCUMENT_FRONT);
        setClientToken("");
        setTokenCreated(0);
        setTemplateType("");
    };

    const uploadImages = async () => {
        setLoading(true);

        const base64Blobs = await Promise.all(
            blobs.map(async (blob) => {
                return await blobToBase64(blob);
            }),
        );
        const doc = new Document({ images: base64Blobs });
        doc.barcode = barcode;

        const formattedPayload = doc.formatPayload(nextStep);
        const response: IUploadImagesResponse = await uploadPhoto(
            leadId,
            formattedPayload,
        );
        setLoading(false);
        return response;
    };

    const state: StoreState = {
        leadId,
        blobs,
        transactionCreated,
        hasCreditConsent,
        barcode,
        nextStep,
        handoffMethod,
        clientToken,
        tokenCreated,
        templateType,
        loading,
        hydrated,
        error,
        template,
    };
    const actions = {
        setBarcode,
        setBlobs,
        setError,
        setHydrated,
        setTransactionCreated,
        setHasCreditConsent,
        setNextStep,
        setClientToken,
        setTokenCreated,
        setTemplateType,
        setHandoffMethod,

        uploadPhoto: uploadImages,
        reset,
        loadTemplate,
    };
    const value = { state, actions };

    return <StoreContext.Provider value={value} {...props} />;
}

export const useStore = () => useContext(StoreContext);
